import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { tokens } from '../../../../design';
import { formatCurrency } from '../../../../utils/format';
import type { ResolveFormValues } from '../../_lib/resolveAppealSchema';

/**
 * Conditional refund inputs for the `refund_partial` outcome (AUT-130,
 * architect 🟠 #1 / #2). Mirrors the Zod refine — at least one of
 * `refundAmount` OR `refundPercentage`. `refundAmount` is clamped
 * `<= orderTotal` by the schema (the backend has no upper cap and resolve
 * moves real WebPay money — the SPA must block submit above it); the hint
 * surfaces the cap so the operator sees it.
 *
 * Both are text inputs (a controlled number input can't hold a partial
 * value cleanly); the schema parses + validates. `refundPercentage` is
 * `0..100` (bounded server-side too).
 */
export interface RefundFieldsProps {
  control: Control<ResolveFormValues>;
  loading: boolean;
  orderTotal: number;
}

export const RefundFields = ({ control, loading, orderTotal }: RefundFieldsProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="caption" sx={{ color: tokens.text.tertiary }}>
        {t('appeals.resolveDialog.refundHint', { total: formatCurrency(orderTotal) })}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Controller
          name="refundAmount"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              size="small"
              inputMode="decimal"
              disabled={loading}
              label={t('appeals.resolveDialog.refundAmountLabel')}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error ? t(fieldState.error.message ?? '') : ' '}
            />
          )}
        />
        <Controller
          name="refundPercentage"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              size="small"
              inputMode="decimal"
              disabled={loading}
              label={t('appeals.resolveDialog.refundPercentageLabel')}
              slotProps={{
                input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
              }}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error ? t(fieldState.error.message ?? '') : ' '}
            />
          )}
        />
      </Box>
    </Box>
  );
};
